/**
 * Citation Parser Utility
 * Handles parsing and validation of citation IDs to extract line numbers
 * Follows Single Responsibility Principle - only handles citation parsing
 */
export class CitationParser {
    static DEBUG_PREFIX = '[CitationParser]';
    /**
     * Parse citation_id to extract line number
     * Expected formats:
     * - "123" (just line number)
     * - "s_ABC123/456" (session_id/line_number)
     * Invalid formats: "turn3file8", non-numeric values
     *
     * @param citationId - The citation ID to parse
     * @returns CitationParseResult with validation status and line number
     */
    static parseCitation(citationId) {
        console.debug(`${this.DEBUG_PREFIX} Parsing citation:`, citationId);
        if (!citationId) {
            console.debug(`${this.DEBUG_PREFIX} No citation provided`);
            return {
                isValid: false,
                error: 'No citation provided',
                originalCitation: ''
            };
        }
        const trimmedCitation = citationId.trim();
        // Check for known invalid formats
        if (this.isInvalidFormat(trimmedCitation)) {
            console.warn(`${this.DEBUG_PREFIX} Invalid citation format detected:`, trimmedCitation);
            return {
                isValid: false,
                error: `Invalid citation format: ${trimmedCitation}`,
                originalCitation: trimmedCitation
            };
        }
        let lineNumber;
        // Check if citation is in format "session_id/line_number"
        if (trimmedCitation.includes('/')) {
            const parts = trimmedCitation.split('/');
            if (parts.length === 2) {
                const lineNumberPart = parts[1];
                lineNumber = parseInt(lineNumberPart, 10);
                console.debug(`${this.DEBUG_PREFIX} Parsed session/line format:`, {
                    sessionId: parts[0],
                    lineNumberPart,
                    lineNumber
                });
            }
            else {
                console.warn(`${this.DEBUG_PREFIX} Invalid session/line format:`, trimmedCitation);
                return {
                    isValid: false,
                    error: 'Invalid session/line format',
                    originalCitation: trimmedCitation
                };
            }
        }
        else {
            // Try to parse as plain line number
            lineNumber = parseInt(trimmedCitation, 10);
        }
        if (isNaN(lineNumber) || lineNumber <= 0) {
            console.warn(`${this.DEBUG_PREFIX} Citation is not a valid line number:`, trimmedCitation);
            return {
                isValid: false,
                error: 'Citation must be a positive number',
                originalCitation: trimmedCitation
            };
        }
        console.debug(`${this.DEBUG_PREFIX} Successfully parsed citation:`, {
            citation: trimmedCitation,
            lineNumber
        });
        return {
            isValid: true,
            lineNumber,
            originalCitation: trimmedCitation
        };
    }
    /**
     * Check if citation matches known invalid formats
     * @param citation - Citation to check
     * @returns true if format is known to be invalid
     */
    static isInvalidFormat(citation) {
        const invalidPatterns = [
            /^turn\d+file\d+$/i,
            /^file\d+$/i,
            /^turn\d+$/i // Matches "turn123" pattern
        ];
        return invalidPatterns.some(pattern => pattern.test(citation));
    }
    /**
     * Generate search suggestion from gem item
     * Used when citation is invalid to suggest text search
     *
     * @param item - Gem item containing quote or text
     * @returns Search suggestion string
     */
    static generateSearchSuggestion(item) {
        // Prefer quote over text for search
        const searchText = item.quote || item.text || item.title || '';
        if (!searchText) {
            return 'Try searching for related keywords';
        }
        // Truncate long text for display
        const maxLength = 50;
        const truncated = searchText.length > maxLength
            ? searchText.substring(0, maxLength) + '...'
            : searchText;
        return `Try searching for: "${truncated}"`;
    }
}
